import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import {
  QuestionAnswer,
  ArrowForward,
  Edit,
  Check,
  Info,
  BusinessCenter,
  Email,
  Close,
} from '@mui/icons-material';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import Modal from './modals/Modal';
import { useInquiry } from '../context/InquiryContext';
import translateStatusToPolish from '../helperFunctions/translateStatusToPolish';
import { Inquiry, InquiryStatus } from '../models/Inquiry';

type Props = {
  inquiry: Inquiry;
  onStatusChange: () => void;
};

const InquiryCard: React.FC<Props> = ({ inquiry, onStatusChange }) => {
  const { changeInquiryStatus } = useInquiry();

  const [open, setOpen] = useState(false);
  const { control, handleSubmit } = useForm({
    defaultValues: {
      status: inquiry.status,
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = async (data: { status: InquiryStatus }) => {
    const toastId = toast.loading('Zapisywanie...', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: 0,
      theme: 'colored',
    });
    try {
      await changeInquiryStatus(inquiry.id, data.status);
      toast.update(toastId, {
        render: 'Status zapisany!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      onStatusChange();
    } catch (error: Error | any) {
      console.error('Failed to change Inquiry status', error);
      toast.update(toastId, {
        render: 'Nie udało się zapisać statusu',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
    setOpen(false);
  };

  // Funkcje dla stylów statusów
  const getInquiryStatusColor = (status: InquiryStatus) => {
    switch (status) {
      case InquiryStatus.COMPLETED:
        return 'bg-green-50 text-green-700 border-green-200';
      case InquiryStatus.IN_PROGRESS:
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case InquiryStatus.CLOSED:
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };

  const getInquiryStatusIcon = (status: InquiryStatus) => {
    switch (status) {
      case InquiryStatus.COMPLETED:
        return <Check fontSize="small" />;
      case InquiryStatus.CLOSED:
        return <Close fontSize="small" />;
      case InquiryStatus.IN_PROGRESS:
        return <Info fontSize="small" />;
      default:
        return <QuestionAnswer fontSize="small" />;
    }
  };

  // Formatowanie daty
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Obliczenie liczby produktów w zapytaniu
  const totalProducts = inquiry.products?.length || 0;

  // Text dla liczby produktów
  const getProductsText = (count: number) => {
    if (count === 1) return '1 produkt';
    if (count > 1 && count < 5) return `${count} produkty`;
    return `${count} produktów`;
  };

  return (
    <motion.div
      className={`bg-white rounded-lg border ${
        inquiry.status === InquiryStatus.CLOSED
          ? 'border-red-200'
          : 'border-gray-200'
      } overflow-hidden hover:shadow-md transition-shadow`}
      whileHover={{ y: -2 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="font-medium text-gray-900 text-base">
              Zapytanie #{inquiry.id}
            </h3>
            <p className="text-sm text-gray-500">
              {formatDate(inquiry.createdAt)}
            </p>
          </div>

          <div
            className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 border ${getInquiryStatusColor(
              inquiry.status,
            )}`}
          >
            {getInquiryStatusIcon(inquiry.status)}
            {translateStatusToPolish(inquiry.status)}
          </div>
        </div>

        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center">
              <BusinessCenter fontSize="small" className="text-gray-400 mr-1" />
              <span className="text-sm font-medium">{inquiry.name}</span>
            </div>

            <div className="flex items-center">
              <Email fontSize="small" className="text-gray-400 mr-1" />
              <span className="text-sm text-gray-600">{inquiry.email}</span>
            </div>
          </div>

          {inquiry.nip && (
            <div className="bg-gray-50 px-2 py-1 rounded text-xs font-medium text-gray-600">
              NIP: {inquiry.nip}
            </div>
          )}
        </div>

        <div className="flex justify-between items-end mb-3">
          <div className="flex items-center">
            <div className="flex -space-x-2 mr-2">
              {inquiry.products?.slice(0, 3).map((item) => (
                <div
                  key={item.productId}
                  className="w-8 h-8 border-2 border-white rounded-full overflow-hidden bg-gray-100"
                >
                  {item.product?.mobilePrimaryImage ? (
                    <img
                      src={item.product.mobilePrimaryImage}
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <span className="text-xs text-gray-500">?</span>
                    </div>
                  )}
                </div>
              ))}

              {(inquiry.products?.length || 0) > 3 && (
                <div className="w-8 h-8 border-2 border-white rounded-full bg-gray-50 flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-600">
                    +{(inquiry.products?.length || 0) - 3}
                  </span>
                </div>
              )}
            </div>

            <span className="text-sm text-gray-600">
              {getProductsText(totalProducts)}
            </span>
          </div>

          {inquiry.remarks && (
            <div className="text-xs text-gray-500 italic max-w-[50%] truncate">
              "{inquiry.remarks}"
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 pt-3 mt-3 flex justify-between items-center">
          <div className="flex gap-2">
            {inquiry.status !== InquiryStatus.CLOSED &&
              inquiry.status !== InquiryStatus.IN_PROGRESS && (
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="py-1 px-2 text-xs font-medium text-primary border border-primary rounded-md flex items-center gap-1 hover:bg-blue-50 transition-colors"
                  onClick={handleClickOpen}
                >
                  <Edit fontSize="small" />
                  Zmień status
                </motion.button>
              )}

            {inquiry.status === InquiryStatus.IN_PROGRESS && (
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="py-1 px-2 text-xs font-medium text-green-600 border border-green-600 rounded-md flex items-center gap-1 hover:bg-green-50 transition-colors"
                onClick={() =>
                  (window.location.href = `/admin/oferty/dodaj/${inquiry.id}`)
                }
              >
                <Check fontSize="small" />
                Utwórz ofertę
              </motion.button>
            )}
          </div>

          <Link
            to={`${inquiry.id}`}
            className="flex items-center text-primary font-medium hover:text-primary-dark transition-colors text-sm"
          >
            Szczegóły
            <ArrowForward fontSize="small" className="ml-1" />
          </Link>
        </div>
      </div>

      {/* Modal zmiany statusu */}
      <Modal show={open} onClose={handleClose} size="sm">
        <div className="p-4">
          <div className="mb-4 flex items-center border-b pb-3">
            <QuestionAnswer className="text-primary mr-2" />
            <h2 className="text-lg font-medium text-gray-900">
              Zmień status zapytania
            </h2>
          </div>

          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Uwaga! Zmiana statusu zapytania spowoduje wysłanie powiadomienia
              do klienta. Zmiana statusu jest nieodwracalna, a w przypadku
              zamknięcia zapytania nie będzie możliwości jego ponownego
              otwarcia.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="mb-5">
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status</InputLabel>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="Status">
                      <MenuItem value={InquiryStatus.IN_PROGRESS}>
                        {translateStatusToPolish(InquiryStatus.IN_PROGRESS)}
                      </MenuItem>
                      <MenuItem value={InquiryStatus.CLOSED}>
                        {translateStatusToPolish(InquiryStatus.CLOSED)}
                      </MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </div>

            <div className="flex justify-end gap-2">
              <motion.button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                onClick={handleClose}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Anuluj
              </motion.button>

              <motion.button
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded-md font-medium hover:bg-primary-dark transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Zapisz zmiany
              </motion.button>
            </div>
          </form>
        </div>
      </Modal>
    </motion.div>
  );
};

export default InquiryCard;
